import { useMemo, useState } from 'react';
import { type NostrEvent } from '@nostrify/nostrify';
import { Link } from 'react-router-dom';
import { nip19 } from 'nostr-tools';
import { useAuthor } from '@/hooks/useAuthor';
import { genUserName } from '@/lib/genUserName';
import { cn } from '@/lib/utils';
import { Dialog, DialogContent, DialogTrigger } from '@/components/ui/dialog';

interface NoteContentProps {
  event: NostrEvent;
  className?: string;
}

/** Check if URL is an image */
function isImageUrl(url: string): boolean {
  const imageExtensions = /\.(jpg|jpeg|png|gif|webp|avif|bmp|svg)$/i;
  return imageExtensions.test(url.split('?')[0]);
}

/** Check if URL is a video */
function isVideoUrl(url: string): boolean {
  const videoExtensions = /\.(mp4|webm|ogg|mov|avi|mkv)$/i;
  return videoExtensions.test(url.split('?')[0]);
}

/** Parses content of text note events so that URLs and hashtags are linkified. */
export function NoteContent({
  event,
  className,
}: NoteContentProps) {
  // Process the content to render mentions, links, etc.
  const content = useMemo(() => {
    const text = event.content;

    // Regex to find URLs, Nostr references, and hashtags
    // Updated to handle all nostr: URI types including naddr1
    const regex = /(https?:\/\/[^\s]+)|nostr:(npub1|note1|nprofile1|nevent1|naddr1)([023456789acdefghjklmnpqrstuvwxyz]+)|(#\w+)/g;

    const parts: React.ReactNode[] = [];
    const mediaUrls: string[] = [];
    let lastIndex = 0;
    let match: RegExpExecArray | null;
    let keyCounter = 0;

    while ((match = regex.exec(text)) !== null) {
      const [fullMatch, url, nostrPrefix, nostrData, hashtag] = match;
      const index = match.index;

      // Add text before this match
      if (index > lastIndex) {
        parts.push(text.substring(lastIndex, index));
      }

      if (url) {
        // Check if URL is an image or video
        if (isImageUrl(url)) {
          mediaUrls.push(url);
          parts.push(
            <EmbeddedImage key={`img-${keyCounter++}`} url={url} />
          );
        } else if (isVideoUrl(url)) {
          mediaUrls.push(url);
          parts.push(
            <EmbeddedVideo key={`vid-${keyCounter++}`} url={url} />
          );
        } else {
          // Regular URL link
          parts.push(
            <a
              key={`url-${keyCounter++}`}
              href={url}
              target="_blank"
              rel="noopener noreferrer"
              className="text-primary hover:underline break-all"
            >
              {url}
            </a>
          );
        }
      } else if (nostrPrefix && nostrData) {
        // Handle Nostr references
        try {
          const nostrId = `${nostrPrefix}${nostrData}`;
          const decoded = nip19.decode(nostrId);

          if (decoded.type === 'npub' || decoded.type === 'nprofile') {
            const pubkey = decoded.type === 'npub' ? decoded.data : decoded.data.pubkey;
            parts.push(
              <NostrMention key={`mention-${keyCounter++}`} pubkey={pubkey} />
            );
          } else if (decoded.type === 'note' || decoded.type === 'nevent') {
            // Link to note
            const noteId = decoded.type === 'note' ?
              nip19.noteEncode(decoded.data) :
              nip19.noteEncode(decoded.data.id);
            parts.push(
              <Link
                key={`note-${keyCounter++}`}
                to={`/${noteId}`}
                className="text-primary hover:underline font-medium"
              >
                nostr:{nostrPrefix}{nostrData.substring(0, 8)}...
              </Link>
            );
          } else if (decoded.type === 'naddr') {
            // Link to addressable event
            parts.push(
              <Link
                key={`naddr-${keyCounter++}`}
                to={`/${nostrId}`}
                className="text-primary hover:underline font-medium"
              >
                nostr:{nostrPrefix}{nostrData.substring(0, 8)}...
              </Link>
            );
          } else {
            // For other types, just show as a link
            parts.push(
              <Link
                key={`nostr-${keyCounter++}`}
                to={`/${nostrId}`}
                className="text-primary hover:underline"
              >
                nostr:{nostrPrefix}{nostrData.substring(0, 8)}...
              </Link>
            );
          }
        } catch {
          // If decoding fails, just render as text
          parts.push(fullMatch);
        }
      } else if (hashtag) {
        // Handle hashtags
        const tag = hashtag.slice(1); // Remove the #
        parts.push(
          <span
            key={`hashtag-${keyCounter++}`}
            className="text-primary font-medium"
          >
            {hashtag}
          </span>
        );
      }

      lastIndex = index + fullMatch.length;
    }

    // Add any remaining text
    if (lastIndex < text.length) {
      parts.push(text.substring(lastIndex));
    }

    // If no special content was found, just use the plain text
    if (parts.length === 0) {
      parts.push(text);
    }

    return parts;
  }, [event]);

  return (
    <div className={cn("whitespace-pre-wrap break-words", className)}>
      {content.length > 0 ? content : event.content}
    </div>
  );
}

// Helper component to display embedded images
function EmbeddedImage({ url }: { url: string }) {
  const [isOpen, setIsOpen] = useState(false);

  return (
    <>
      <Dialog open={isOpen} onOpenChange={setIsOpen}>
        <DialogTrigger asChild>
          <div className="my-2 cursor-pointer">
            <img
              src={url}
              alt="Embedded content"
              className="max-w-full h-auto rounded-lg border hover:opacity-90 transition-opacity"
              loading="lazy"
              onClick={() => setIsOpen(true)}
            />
          </div>
        </DialogTrigger>
        <DialogContent className="max-w-4xl p-0">
          <img
            src={url}
            alt="Full size"
            className="w-full h-auto"
          />
        </DialogContent>
      </Dialog>
    </>
  );
}

// Helper component to display embedded videos
function EmbeddedVideo({ url }: { url: string }) {
  return (
    <div className="my-2">
      <video
        src={url}
        controls
        className="max-w-full h-auto rounded-lg border"
        preload="metadata"
      >
        Your browser does not support the video tag.
      </video>
    </div>
  );
}

// Helper component to display user mentions
function NostrMention({ pubkey }: { pubkey: string }) {
  const author = useAuthor(pubkey);
  const npub = nip19.npubEncode(pubkey);
  const hasRealName = !!author.data?.metadata?.name;
  const displayName = author.data?.metadata?.name ?? genUserName(pubkey);

  return (
    <Link
      to={`/${npub}`}
      className={cn(
        "font-medium hover:underline",
        hasRealName
          ? "text-blue-500"
          : "text-gray-500 hover:text-gray-700"
      )}
    >
      @{displayName}
    </Link>
  );
}